/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

import { useEffect, useState } from "react";
import { supabase } from "../utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../utils/accountService.js";

/**
 * Which modules are launched, from the `modules` catalog (migration 0066).
 *
 * This answers "have WE finished building it?" — a Brand Command staff
 * decision, flipped in Admin → Modules. It is NOT the billing question: a
 * tenant still needs the plan entitlement (`subscriptions.entitled_modules`,
 * enforced in RLS by account_has_module) to actually use a live module.
 * Both gates apply; neither replaces the other.
 *
 * A module that isn't live is still visible as a "Soon" tile — that's the
 * roadmap signal customers already expect — but it has no link, and the route
 * guard turns it away if someone types the URL.
 *
 * Fails OPEN on error, deliberately. If this read breaks, showing the launcher
 * as it was is far better than blanking every tile for every customer; the
 * route guard and RLS are the real boundaries, this is presentation.
 */
export function useLiveModules() {
  const [modules, setModules] = useState(null);   // null = still loading
  const [accountId, setAccountId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const [catalog, acct] = await Promise.all([
        supabase.from("modules").select("module_id, is_live, route, beta_account_ids"),
        getMyPrimaryAccount().catch(() => null),
      ]);
      if (cancelled) return;
      if (catalog.error) {
        console.error("[useLiveModules]", catalog.error);
        setModules([]);            // fail open — see note above
        return;
      }
      setAccountId(acct?.accountId ?? null);
      setModules(catalog.data ?? []);
    })();

    return () => { cancelled = true; };
  }, []);

  /**
   * Is this module launched for the current tenant?
   * Unknown ids return true so a newly-registered module that hasn't been added
   * to the catalog yet doesn't silently vanish from the launcher.
   */
  const isLive = (moduleId) => {
    if (modules === null) return true;             // still loading — don't flicker
    const row = modules.find((m) => m.module_id === moduleId);
    if (!row) return true;                          // not catalogued yet
    if (row.is_live) return true;
    // Finished but unannounced: named tenants can pilot it.
    return !!(accountId && (row.beta_account_ids || []).includes(accountId));
  };

  /**
   * Same question, keyed by route. The launcher joins on route because its own
   * tile keys and the catalog's module_ids were coined at different times and
   * don't all match ("incentives" vs "incentive-tracker"), whereas the route is
   * the same string in both by definition.
   */
  const isLiveRoute = (route) => {
    if (!route) return false;                       // no screen — can't be live
    if (modules === null) return true;              // still loading — don't flicker
    const row = modules.find((m) => m.route === route);
    if (!row) return true;                          // not catalogued yet
    if (row.is_live) return true;
    return !!(accountId && (row.beta_account_ids || []).includes(accountId));
  };

  return { modules, isLive, isLiveRoute, loading: modules === null };
}
